import React, { useState, useRef, useEffect } from 'react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useResponsiveDrag } from 'services/useResponsiveDrag';

// ##### RowTodoItem Component - Simplified version with integrated drag functionality
export default function RowTodoItem({ 
  item, 
  onToggleItem, 
  onDeleteItem, 
  onEditItem,
  isEditMode = false
}) {
  // ##### Integrated useSortable - No wrapper needed
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });
  const [inputValue, setInputValue] = useState(item.text);
  const textareaRef = useRef(null);
  const { dragHandleClass, dragHandleStyle } = useResponsiveDrag();

  // ##### Auto-resize textarea on content change
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  // ##### Adjust height when input value changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    // Update global state and backend only when losing focus
    if (inputValue !== item.text) {
      onEditItem?.(item.id, inputValue);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Update global state and backend on Enter
      if (inputValue !== item.text) {
        onEditItem?.(item.id, inputValue);
      }
    }
  };

  // ##### ReadOnly Mode - Simple display without drag functionality
  if (!isEditMode) {
    return (
      <li className="flex justify-between items-start bg-white py-1">
        <div className="flex items-start min-w-0 flex-1">
          <input
            type="checkbox"
            checked={item.done}
            onChange={() => onToggleItem && onToggleItem(item.id)}
            onClick={(e) => e.stopPropagation()}
            className="mr-2 mt-2"
          />
          <span
            className={`${item.done ? 'line-through text-gray-400' : 'text-gray-700'} whitespace-pre-wrap break-words`}
          >
            {item.text || <span className="text-gray-400 italic">Empty item</span>}
          </span>
        </div>
      </li>
    );
  }

  // ##### Edit Mode - With drag functionality
  return (
    <li 
      ref={setNodeRef}
      className={`flex items-start bg-white py-1 ${isDragging ? 'opacity-0' : ''}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      {/* Drag Handle */}
      <div className="w-8 flex-shrink-0 flex items-center justify-center">
        <button
          {...attributes}
          {...listeners}
          className={`w-6 h-6 ${dragHandleClass}`}
          style={dragHandleStyle}
          tabIndex={-1}
          type="button"
          onTouchStart={(e) => e.stopPropagation()}
        >
          <Bars3Icon className="w-4 h-4 text-gray-400" />
        </button>
      </div>
      
      {/* Content Area */}
      <div className="flex-1 flex items-start min-w-0">
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={item.done}
          onChange={() => onToggleItem && onToggleItem(item.id)}
          className="mr-2 mt-2 flex-shrink-0"
        />
        
        {/* Textarea */}
        <textarea
          ref={textareaRef}
          className={`border-b px-1 py-0.5 text-sm flex-1 outline-none resize-none overflow-hidden ${
            item.done ? 'line-through text-gray-400 bg-gray-50 border-gray-200' : 'border-gray-300'
          }`}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          disabled={item.done}
          rows={1}
          style={{ 
            minHeight: '1.5rem',
            height: 'auto',
            lineHeight: '1.5rem'
          }}
          placeholder="Enter task item..."
          onTouchStart={(e) => e.stopPropagation()}
        />
        
        {/* Delete Button */}
        {onDeleteItem && (
          <button
            type="button"
            className="ml-2 p-1 hover:bg-gray-100 rounded-full flex-shrink-0 mt-0.5 w-6 h-6 flex items-center justify-center"
            onClick={() => onDeleteItem(item.id)}
          >
            <XMarkIcon className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>
    </li>
  );
}
